import React, { useState, useEffect } from "react";
import { IoIosClose, IoMdShare } from "react-icons/io";
import { FaWhatsapp, FaDownload } from "react-icons/fa";
import "../assets/css/VisitPrescriptionSm.css";
import { useTranslation } from "react-i18next";
import { secureAxios } from "../apis/authSetup";
import { generatePrescriptionHTML } from "../utils/prescriptionGenerator";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import moment from "moment"; // Added moment import

const VisitPrescriptionSm = ({
    setShowModal,
    consultationData,
    handlePrint,
}) => {
    const { t } = useTranslation();
    const [pdfUrl, setPdfUrl] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchOrGeneratePDF = async () => {
            if (!consultationData) return;

            if (consultationData.pdf_url) {
                setPdfUrl(consultationData.pdf_url);
                return;
            }

            // Generate PDF
            setLoading(true);
            try {
                // 1. Generate HTML
                const htmlContent = generatePrescriptionHTML(
                    consultationData,
                    consultationData.personal_details,
                    consultationData.prescription_setup
                );

                // 2. Prepare Payload
                const pageSize =
                    consultationData.prescription_setup?.pageSize || "A4";
                const headerFooterSetup =
                    consultationData.header_footer?.[pageSize] ||
                    consultationData.header_footer?.["A4"] ||
                    {};

                const payload = {
                    consultation_id: consultationData.id,
                    html_content: htmlContent,
                    patient_id: consultationData.personal_details?.id,
                    clinic_id: consultationData.clinic_id?.id,
                    doctor_id: consultationData.doctor_user,
                    header_footer_setup: headerFooterSetup,
                    settings: consultationData.prescription_setup,
                    page_size: pageSize,
                };

                const response = await secureAxios.post(
                    "/report/generate-prescription-pdf/",
                    payload
                );

                if (response?.pdf_url) {
                    setPdfUrl(response.pdf_url);
                } else {
                    setError("Failed to retrieve PDF URL");
                }
            } catch (err) {
                console.error("PDF Generation Error:", err);
                setError("Failed to generate PDF");
            } finally {
                setLoading(false);
            }
        };

        fetchOrGeneratePDF();
    }, [consultationData]);

    const handleShare = async () => {
        if (!pdfUrl) return;

        try {
            // Fetch the PDF as a blob
            const response = await fetch(pdfUrl);
            const blob = await response.blob();

            // Create a file from the blob
            const file = new File(
                [blob],
                `Prescription_${consultationData?.personal_details?.full_name || "Patient"}.pdf`,
                {
                    type: "application/pdf",
                }
            );

            // Check if sharing files is supported
            if (navigator.canShare && navigator.canShare({ files: [file] })) {
                await navigator.share({
                    files: [file],
                    title: "Prescription",
                    text: `Prescription for ${consultationData?.personal_details?.full_name}`,
                });
            } else {
                // Fallback if file sharing is not supported: Just download it
                // We do NOT share the link as requested
                handleDownload();
            }
        } catch (error) {
            console.error("Error sharing file:", error);
            // If sharing fails, standard download fallback
            handleDownload();
        }
    };

    const handleDownload = async () => {
        if (!pdfUrl) return;

        try {
            setLoading(true); // Show loading while fetching blob
            const response = await fetch(pdfUrl);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);

            const link = document.createElement("a");
            link.href = url;
            link.download = `Prescription_${consultationData?.personal_details?.full_name || "Patient"}.pdf`;
            document.body.appendChild(link);
            link.click();

            // Cleanup
            window.URL.revokeObjectURL(url);
            document.body.removeChild(link);
        } catch (error) {
            console.error("Download Error:", error);
            // Fallback (though rare)
            const link = document.createElement("a");
            link.href = pdfUrl;
            link.download = `Prescription.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-white z-50 flex flex-col">
            {/* Minimal Header with consolidated Actions */}
            <div className="px-4 py-3 flex justify-between items-center bg-white border-b border-gray-100 shadow-sm shrink-0 z-10">
                <div className="flex flex-col">
                    <span className="text-lg font-bold text-gray-800 leading-tight">
                        Prescription
                    </span>
                    <span className="text-xs text-gray-500">
                        {moment().format("MMMM DD, YYYY")}
                    </span>
                </div>
                <div className="flex items-center gap-3">
                    {/* Share Button (Primary Action) */}
                    {pdfUrl && !loading && (
                        <button
                            onClick={handleShare}
                            className="p-2 text-blue-600 bg-blue-50 rounded-full hover:bg-blue-100 transition-colors"
                            aria-label="Share"
                        >
                            <IoMdShare size={22} />
                        </button>
                    )}
                    {/* Download Button */}
                    {pdfUrl && !loading && (
                        <button
                            onClick={handleDownload}
                            className="p-2 text-green-600 bg-green-50 rounded-full hover:bg-green-100 transition-colors"
                            aria-label="Download"
                        >
                            <FaDownload size={20} />
                        </button>
                    )}
                    {/* Close Button */}
                    <button
                        onClick={() => setShowModal(false)}
                        className="p-1 text-gray-500 hover:text-gray-800 transition-colors ml-1"
                    >
                        <IoIosClose size={32} />
                    </button>
                </div>
            </div>

            {/* Viewer Area - Takes all remaining space */}
            <div className="flex-1 relative w-full overflow-hidden bg-gray-100">
                {loading && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-white z-20">
                        <AiOutlineLoading3Quarters className="animate-spin text-4xl text-green-600 mb-2" />
                        <p className="text-gray-600 font-medium">
                            Preparing...
                        </p>
                    </div>
                )}

                {error && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-white z-20 text-red-500 px-4 text-center">
                        <p className="font-semibold mb-2">Unavailable</p>
                        <p className="text-sm">{error}</p>
                    </div>
                )}

                {!loading && !error && pdfUrl && (
                    <iframe
                        src={pdfUrl}
                        className="w-full h-full border-none"
                        title="Prescription Viewer"
                    />
                )}

                {!loading && !error && !pdfUrl && (
                    <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                        No Document Found
                    </div>
                )}
            </div>
        </div>
    );
};

export default VisitPrescriptionSm;
